<template>
  <PainelLayout>
    <Breadcrumb :lista="[
      { 'name':'Painel', 'link' : '/' },
      { 'name':'Vitrine online', 'link' : '/showcase/settings' }]"
    />

    <div class="row">
      <div class="col-12">
        <h1>Frete automático</h1>
        <p class="text-muted">A vitrine calcula o frete pelo CEP da festa e soma ao pedido. O valor pode ser ajustado no pedido.</p>
      </div>
    </div>
    <hr/>

    <div v-if="loading" class="d-flex justify-content-center p-3">
      <div class="spinner-border" style="width: 4rem; height: 4rem;" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <form v-else @submit.prevent="salvar" class="row g-4">
      <div class="col-lg-7">
        <div class="form-check form-switch mb-4">
          <input v-model="cfg.freight_enabled" class="form-check-input" type="checkbox" role="switch" id="chkFrete">
          <label class="form-check-label" for="chkFrete"><strong>Frete automático ativo</strong></label>
        </div>

        <!-- Base de saída -->
        <div class="card mb-4">
          <div class="card-body">
            <h5 class="card-title">Base de saída</h5>
            <p class="small text-muted">De onde saem as entregas. O cliente não vê esse endereço.</p>
            <div class="row g-2 align-items-end mb-3">
              <div class="col-5">
                <label class="form-label" for="txtCep">CEP</label>
                <input v-model="cfg.base_zipcode" maxlength="10" class="form-control" id="txtCep" placeholder="00000-000">
              </div>
              <div class="col-3">
                <label class="form-label" for="txtNum">Número</label>
                <input v-model="cfg.base_number" maxlength="20" class="form-control" id="txtNum">
              </div>
              <div class="col-4">
                <button type="button" class="btn btn-outline-primary w-100" @click="localizar" :disabled="localizando || !cfg.base_zipcode">
                  <span v-if="localizando" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Localizar
                </button>
              </div>
            </div>
            <p v-if="enderecoBase" class="small mb-2"><i class="bi bi-geo-alt me-1"></i>{{ enderecoBase }}</p>
            <BasePinMap :lat="cfg.base_lat" :lng="cfg.base_lng" @update="p => { cfg.base_lat = p.lat; cfg.base_lng = p.lng; }" />
          </div>
        </div>

        <!-- Regras -->
        <div class="card mb-4">
          <div class="card-body row g-3">
            <h5 class="card-title mb-0">Regras de cobrança</h5>
            <div class="col-md-6">
              <label class="form-label" for="txtRaio">Raio grátis (km)</label>
              <input v-model.number="cfg.free_radius_km" type="number" min="0" step="0.1" class="form-control" id="txtRaio">
            </div>
            <div class="col-md-6">
              <label class="form-label" for="txtKm">Valor por km excedente (R$)</label>
              <input v-model.number="cfg.price_per_km" type="number" min="0" step="0.01" class="form-control" id="txtKm">
            </div>
            <div class="col-md-4">
              <label class="form-label" for="txtMin">Frete mínimo (R$)</label>
              <input v-model="cfg.freight_min" type="number" min="0" step="0.01" class="form-control" id="txtMin" placeholder="Sem mínimo">
            </div>
            <div class="col-md-4">
              <label class="form-label" for="txtMax">Frete máximo (R$)</label>
              <input v-model="cfg.freight_max" type="number" min="0" step="0.01" class="form-control" id="txtMax" placeholder="Sem máximo">
            </div>
            <div class="col-md-4">
              <label class="form-label" for="txtAcima">Grátis acima de (R$)</label>
              <input v-model="cfg.free_above_amount" type="number" min="0" step="0.01" class="form-control" id="txtAcima" placeholder="Desligado">
            </div>
            <p class="small text-muted mb-0">
              A distância é de carro (não em linha reta). Ordem das regras: grátis acima do valor → preço fixo da cidade → raio grátis → km excedente × valor por km (respeitando mínimo e máximo).
            </p>
          </div>
        </div>

        <!-- Preço fixo por cidade -->
        <div class="card mb-4">
          <div class="card-body">
            <h5 class="card-title">Preço fixo por cidade</h5>
            <p class="small text-muted">Opcional. Tem prioridade sobre o cálculo por km.</p>
            <div v-for="(c, i) in cfg.cities" :key="i" class="row g-2 align-items-center mb-2">
              <div class="col-6">
                <input v-model="c.city" required maxlength="120" class="form-control form-control-sm" placeholder="Cidade" aria-label="Cidade">
              </div>
              <div class="col-2">
                <input v-model="c.state" required maxlength="2" class="form-control form-control-sm text-uppercase" placeholder="UF" aria-label="UF">
              </div>
              <div class="col-3">
                <input v-model.number="c.price" required type="number" min="0" step="0.01" class="form-control form-control-sm" placeholder="R$" aria-label="Preço">
              </div>
              <div class="col-1 text-end">
                <button type="button" class="btn btn-sm btn-outline-danger" @click="cfg.cities.splice(i, 1)" aria-label="Remover cidade"><i class="bi bi-trash"></i></button>
              </div>
            </div>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="cfg.cities.push({ city: '', state: '', price: null })">
              <i class="bi bi-plus me-1"></i>Adicionar cidade
            </button>
          </div>
        </div>

        <button type="submit" class="btn btn-primary" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          Salvar
        </button>
      </div>

      <div class="col-lg-5">
        <!-- Simulador -->
        <div class="card mb-4">
          <div class="card-body">
            <h5 class="card-title">Simular frete</h5>
            <p class="small text-muted">Usa a configuração salva.</p>
            <div class="row g-2">
              <div class="col-6"><input v-model="sim.zipcode" maxlength="10" class="form-control form-control-sm" placeholder="CEP da festa" aria-label="CEP da festa"></div>
              <div class="col-6"><input v-model="sim.number" maxlength="20" class="form-control form-control-sm" placeholder="Número" aria-label="Número"></div>
              <div class="col-6"><input v-model.number="sim.subtotal" type="number" min="0" class="form-control form-control-sm" placeholder="Valor do pedido" aria-label="Valor do pedido"></div>
              <div class="col-6">
                <button type="button" class="btn btn-sm btn-outline-primary w-100" @click="simular" :disabled="simulando || !sim.zipcode">
                  <span v-if="simulando" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span v-else>Simular</span>
                </button>
              </div>
            </div>
            <div v-if="simResult" class="alert alert-light border mt-3 mb-0 small">
              <div v-if="simResult.address">{{ simResult.address.address }}, {{ simResult.address.city }}/{{ simResult.address.state }}</div>
              <div v-if="simResult.distance_km !== null">Distância de carro: <strong>{{ simResult.distance_km }} km</strong></div>
              <div>Frete: <strong>{{ simResult.freight === null ? 'a combinar' : money(simResult.freight) }}</strong> <span class="text-muted">({{ regraLabel(simResult.rule) }})</span></div>
            </div>
          </div>
        </div>

        <!-- Calculadora do custo por km -->
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">Calculadora do custo por km</h5>
            <p class="small text-muted">Estimativa considerando ida e volta do veículo.</p>
            <div class="row g-2">
              <div class="col-6">
                <label class="form-label small" for="calcComb">Combustível (R$/L)</label>
                <input v-model.number="calc.fuel" type="number" min="0" step="0.01" class="form-control form-control-sm" id="calcComb">
              </div>
              <div class="col-6">
                <label class="form-label small" for="calcCons">Consumo (km/L)</label>
                <input v-model.number="calc.kmPerLiter" type="number" min="0.1" step="0.1" class="form-control form-control-sm" id="calcCons">
              </div>
              <div class="col-6">
                <label class="form-label small" for="calcExtra">Outros custos (R$/km)</label>
                <input v-model.number="calc.extra" type="number" min="0" step="0.01" class="form-control form-control-sm" id="calcExtra">
              </div>
              <div class="col-6">
                <label class="form-label small" for="calcMargem">Margem (%)</label>
                <input v-model.number="calc.margin" type="number" min="0" step="1" class="form-control form-control-sm" id="calcMargem">
              </div>
            </div>
            <p class="mt-3 mb-2">Sugestão: <strong>{{ money(sugestaoKm) }}</strong> por km</p>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="cfg.price_per_km = sugestaoKm">Usar esse valor</button>
          </div>
        </div>
      </div>
    </form>
  </PainelLayout>
</template>

<script>
import PainelLayout from '@/layouts/PainelLayout.vue';
import { onMounted, ref, reactive, computed } from 'vue';
import { freight } from '@/models/freight';
import Breadcrumb from '@/components/itens/breadcrumb.vue';
import BasePinMap from '@/components/BasePinMap.vue';
import { useToastStore } from '@/stores/toastStore';
import { parseError } from '@/utils/errorHelper';

const REGRAS = {
  free_above: 'grátis pelo valor do pedido',
  city: 'preço fixo da cidade',
  free_radius: 'dentro do raio grátis',
  distance: 'por km',
  unavailable: 'não foi possível calcular',
  disabled: 'frete automático desligado',
};

export default {
  name: "FreightSettingsView",
  components: { PainelLayout, Breadcrumb, BasePinMap },
  setup() {

    const toastStore = useToastStore();
    const model = freight();
    const loading = ref(false);
    const saving = ref(false);
    const localizando = ref(false);
    const simulando = ref(false);
    const enderecoBase = ref('');
    const simResult = ref(null);

    const cfg = reactive({
      freight_enabled: false,
      base_zipcode: '', base_number: '', base_lat: null, base_lng: null,
      free_radius_km: 0, price_per_km: 0,
      freight_min: '', freight_max: '', free_above_amount: '',
      cities: []
    });
    const sim = reactive({ zipcode: '', number: '', subtotal: null });
    const calc = reactive({ fuel: 6, kmPerLiter: 8, extra: 0.5, margin: 30 });

    // Ida e volta: cada km cobrado do cliente custa 2 km rodados
    const sugestaoKm = computed(() => {
      if (!calc.kmPerLiter) return 0;
      const custo = (calc.fuel / calc.kmPerLiter + (calc.extra || 0)) * 2;
      return Math.round(custo * (1 + (calc.margin || 0) / 100) * 100) / 100;
    });

    // Campos opcionais: vazio vai como null para o backend
    const opcional = v => (v === '' || v === null || v === undefined ? null : Number(v));

    function preencher(data) {
      Object.assign(cfg, {
        freight_enabled: !!data.freight_enabled,
        base_zipcode: data.base_zipcode ?? '',
        base_number: data.base_number ?? '',
        base_lat: data.base_lat !== null ? Number(data.base_lat) : null,
        base_lng: data.base_lng !== null ? Number(data.base_lng) : null,
        free_radius_km: Number(data.free_radius_km ?? 0),
        price_per_km: Number(data.price_per_km ?? 0),
        freight_min: data.freight_min ?? '',
        freight_max: data.freight_max ?? '',
        free_above_amount: data.free_above_amount ?? '',
        cities: (data.cities || []).map(c => ({ city: c.city, state: c.state, price: Number(c.price) })),
      });
    }

    async function localizar() {
      localizando.value = true;
      try {
        const res = await model.locateBase(cfg.base_zipcode, cfg.base_number);
        cfg.base_lat = res.lat;
        cfg.base_lng = res.lng;
        enderecoBase.value = `${res.address.address}, ${cfg.base_number || 's/n'} - ${res.address.city}/${res.address.state}`;
        toastStore.success('Endereço localizado. Confira o pino no mapa.');
      } catch (error) {
        toastStore.warning(parseError(error));
      }
      localizando.value = false;
    }

    async function salvar() {
      saving.value = true;
      try {
        preencher(await model.update({
          ...cfg,
          free_radius_km: Number(cfg.free_radius_km || 0),
          price_per_km: Number(cfg.price_per_km || 0),
          freight_min: opcional(cfg.freight_min),
          freight_max: opcional(cfg.freight_max),
          free_above_amount: opcional(cfg.free_above_amount),
          cities: cfg.cities.map(c => ({ ...c, state: (c.state || '').toUpperCase() })),
        }));
        toastStore.success('Configuração de frete salva.');
      } catch (error) {
        toastStore.error('Erro ao salvar: ' + parseError(error));
      }
      saving.value = false;
    }

    async function simular() {
      simulando.value = true;
      try {
        simResult.value = await model.simulate(sim.zipcode, sim.number, sim.subtotal || 0);
      } catch (error) {
        toastStore.error(parseError(error));
      }
      simulando.value = false;
    }

    function regraLabel(regra) {
      return REGRAS[regra] ?? regra;
    }

    function money(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    onMounted(async () => {
      loading.value = true;
      try {
        preencher(await model.getOne());
      } catch (error) {
        toastStore.error('Erro ao carregar configuração: ' + parseError(error));
      }
      loading.value = false;
    });

    return {
      cfg, sim, calc, loading, saving, localizando, simulando, enderecoBase, simResult, sugestaoKm,
      localizar, salvar, simular, regraLabel, money
    }
  }
}
</script>
